import { useEffect, useState, useCallback } from "react";
import { ArrowLeft, X, Plus, Star } from "lucide-react";
import api from "@/lib/api";

/**
 * Structure:
 *   [{ label: "Catégorie", tiles: ["Vignette 1", "Vignette 2", ...] }, ...]
 * Chaque tile correspond à la valeur `categorie` en base (côté admin).
 */
export default function TabletCarte({ carte, title, subtitle, structure }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTile, setActiveTile] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/cartes/${carte}`);
      setItems(data);
    } catch {
    } finally {
      setLoading(false);
    }
  }, [carte]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const tileItems = activeTile ? items.filter((it) => it.categorie === activeTile) : [];
  const isSpecial = carte === "bistro" && (activeTile === "Formules (du jour & de saison)" || activeTile === "Menu Enfant");

  return (
    <div className="tablet-mode min-h-screen bg-[#0A0A0A] text-[#FDFBF7] overflow-y-auto" data-testid={`carte-${carte}`}>
      <header className="sticky top-0 z-10 bg-[#0A0A0A] border-b border-white/10 px-10 md:px-16 py-8 flex items-center justify-between">
        <div className="flex items-center gap-5">
          <img src="/images/logo.png" alt="" className="h-20 w-20 object-contain" />
          <div>
            <h1 className="font-serif text-5xl leading-none">{title}</h1>
            <p className="text-white/60 text-sm uppercase tracking-[0.3em] mt-2">
              {subtitle}
            </p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-[0.3em] text-white/50">Viriat · 01440</div>
          <div className="font-serif text-2xl mt-1">04 74 45 35 10</div>
        </div>
      </header>

      <main className="px-10 md:px-16 py-12 max-w-7xl mx-auto">
        {loading && <p className="text-center text-white/60 text-xl">Chargement…</p>}

        <div className="space-y-16">
          {structure.map((cat) => (
            <section key={cat.label} data-testid={`cat-${cat.label}`}>
              <div className="flex items-baseline gap-6 mb-8">
                <h2 className="font-serif text-4xl text-[#C64950] uppercase tracking-[0.05em]">{cat.label}</h2>
                <span className="flex-1 h-px bg-white/20" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                {cat.tiles.map((tile) => {
                  const isSpecialTile = carte === "bistro" && (tile === "Formules (du jour & de saison)" || tile === "Menu Enfant");
                  const count = items.filter((it) => it.categorie === tile).length;
                  const displayLabel = tile === "Formules (du jour & de saison)" ? "Nos menus" : tile;
                  const badge = isSpecialTile
                    ? "Découvrir"
                    : count > 0
                    ? `${count} ${count > 1 ? "choix" : "choix"}`
                    : "À venir";
                  return (
                    <button
                      key={tile}
                      data-testid={`tile-${tile}`}
                      onClick={() => setActiveTile(tile)}
                      className="group relative aspect-[5/3] bg-[#1A1A1A] border border-white/10 hover:border-[#A61C24] hover:bg-[#A61C24] transition-all duration-200 flex flex-col items-center justify-center p-6 text-center"
                    >
                      <span className="font-serif text-2xl md:text-3xl text-white leading-tight">{displayLabel}</span>
                      <span className="mt-3 text-xs uppercase tracking-[0.25em] text-white/60 group-hover:text-white/90">
                        {badge}
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <footer className="mt-20 pt-8 border-t border-white/10 text-center text-white/40 text-sm uppercase tracking-[0.3em]">
          Prix nets · Service compris
        </footer>
      </main>

      {activeTile && !isSpecial && (
        <TileOverlay
          title={activeTile}
          items={tileItems}
          onClose={() => setActiveTile(null)}
        />
      )}
      {activeTile === "Formules (du jour & de saison)" && (
        <FormulesOverlay onClose={() => setActiveTile(null)} />
      )}
      {activeTile === "Menu Enfant" && (
        <MenuEnfantOverlay onClose={() => setActiveTile(null)} />
      )}
    </div>
  );
}

function SpecialHeader({ title, onClose }) {
  return (
    <header className="sticky top-0 bg-[#0A0A0A] border-b border-white/10 px-10 md:px-16 py-6 grid grid-cols-[1fr_auto_1fr] items-center z-10">
      <button
        onClick={onClose}
        data-testid="overlay-back"
        className="justify-self-start flex items-center gap-3 text-white/80 hover:text-white text-sm uppercase tracking-[0.25em]"
      >
        <ArrowLeft size={20} /> Retour
      </button>
      <h2 className="font-serif text-3xl md:text-4xl text-[#C64950] text-center whitespace-nowrap">{title}</h2>
      <button onClick={onClose} aria-label="Fermer" className="justify-self-end text-white/70 hover:text-white p-2">
        <X size={24} />
      </button>
    </header>
  );
}

function FormulesOverlay({ onClose }) {
  const [plats, setPlats] = useState([]);
  const [express, setExpress] = useState(null);
  const [saison, setSaison] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, me, f] = await Promise.all([
        api.get("/plats-jour"),
        api.get("/menu-express"),
        api.get("/formules"),
      ]);
      setPlats(p.data);
      setExpress(me.data);
      setSaison(f.data.find((x) => x.type === "saison") || null);
    } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const JS_DAY = ["dimanche","lundi","mardi","mercredi","jeudi","vendredi","samedi"];
  const today = JS_DAY[new Date().getDay()];
  const platsToday = plats.filter((p) => p.jour === today);

  return (
    <div className="fixed inset-0 z-50 bg-[#0A0A0A] text-white overflow-y-auto tablet-mode" data-testid="formules-overlay">
      <SpecialHeader title="Nos menus" onClose={onClose} />
      <main className="px-10 md:px-20 py-14 max-w-5xl mx-auto">
        {/* FORMULE EXPRESS */}
        <section className="mb-20">
          <div className="text-center mb-10">
            <p className="font-script text-2xl text-[#C64950]">Le midi en semaine</p>
            <h3 className="font-serif text-5xl md:text-6xl mt-2">Formule Express</h3>
            <p className="mt-5 text-white/60 italic max-w-xl mx-auto leading-relaxed">
              À composer selon votre faim : <span className="text-[#C64950] not-italic">plat seul</span>, <span className="text-[#C64950] not-italic">plat + entrée</span>, <span className="text-[#C64950] not-italic">plat + dessert</span>, ou <span className="text-[#C64950] not-italic">les trois</span>.
            </p>
            <p className="mt-2 text-xs uppercase tracking-[0.3em] text-white/40">
              Hors samedi · Hors jours fériés · Hors mois d'août
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {/* PLAT DU JOUR */}
            <ExpressCard eyebrow={`Plat du jour · ${today}`} icon={<Star size={16} strokeWidth={2.5} fill="currentColor" />}>
              {platsToday.length === 0 ? (
                <p className="text-white/50 italic text-center">Aucun plat publié.</p>
              ) : (
                platsToday.map((p) => (
                  <div key={p.id} className="text-center">
                    <h4 className="font-serif text-2xl leading-tight">{p.nom}</h4>
                    {p.description && <p className="text-white/60 italic text-sm mt-2">{p.description}</p>}
                    {p.prix != null && (
                      <p className="mt-4 text-2xl price-tag text-[#C64950]">{Number(p.prix).toFixed(2)} €</p>
                    )}
                  </div>
                ))
              )}
            </ExpressCard>

            {/* ENTRÉES */}
            <ExpressCard
              eyebrow="Entrée du jour"
              icon={<Plus size={18} strokeWidth={3} />}
              priceLabel={express?.prix_entree != null ? `${Number(express.prix_entree).toFixed(2)} €` : null}
            >
              {express && (express.entree_1 || express.entree_2) ? (
                <div className="space-y-3 text-center">
                  {express.entree_1 && <p className="font-serif text-xl">{express.entree_1}</p>}
                  {express.entree_1 && express.entree_2 && (
                    <p className="font-script text-xl text-[#C64950]">ou</p>
                  )}
                  {express.entree_2 && <p className="font-serif text-xl">{express.entree_2}</p>}
                </div>
              ) : (
                <p className="text-white/50 italic text-center">–</p>
              )}
            </ExpressCard>

            {/* DESSERTS */}
            <ExpressCard
              eyebrow="Dessert du jour"
              icon={<Plus size={18} strokeWidth={3} />}
              priceLabel={express?.prix_dessert != null ? `${Number(express.prix_dessert).toFixed(2)} €` : null}
            >
              {express && (express.dessert_1 || express.dessert_2) ? (
                <div className="space-y-3 text-center">
                  {express.dessert_1 && <p className="font-serif text-xl">{express.dessert_1}</p>}
                  {express.dessert_1 && express.dessert_2 && (
                    <p className="font-script text-xl text-[#C64950]">ou</p>
                  )}
                  {express.dessert_2 && <p className="font-serif text-xl">{express.dessert_2}</p>}
                </div>
              ) : (
                <p className="text-white/50 italic text-center">–</p>
              )}
            </ExpressCard>
          </div>
        </section>

        {/* FORMULE SAISON */}
        {saison && (
          <section className="pt-14 border-t border-white/10">
            <div className="text-center mb-10">
              <p className="font-script text-2xl text-[#C64950]">Au fil des saisons</p>
              <h3 className="font-serif text-5xl md:text-6xl mt-2">{saison.nom}</h3>
              {saison.prix != null && (
                <div className="inline-block mt-6 px-6 py-2 border border-[#C64950] text-[#C64950] price-tag text-3xl">
                  {saison.prix.toFixed(2)} €
                </div>
              )}
              {saison.description && (
                <p className="mt-5 text-white/70 italic max-w-2xl mx-auto leading-relaxed whitespace-pre-line">
                  {saison.description}
                </p>
              )}
            </div>

            <div className="grid md:grid-cols-3 gap-0 border border-white/10 bg-[#141414] divide-y md:divide-y-0 md:divide-x divide-white/10">
              {saison.entrees?.length > 0 && <SaisonColumn label="Entrées" items={saison.entrees} />}
              {saison.plats?.length > 0 && <SaisonColumn label="Plats" items={saison.plats} />}
              {saison.desserts?.length > 0 && <SaisonColumn label="Desserts" items={saison.desserts} />}
            </div>

            <p className="mt-8 text-center text-sm text-white/50 italic">
              Toutes les entrées, plats et desserts du menu ne peuvent être vendus en dehors de celui-ci.
            </p>
          </section>
        )}
      </main>
    </div>
  );
}

function ExpressCard({ eyebrow, icon, priceLabel, children }) {
  return (
    <div className="relative bg-[#141414] border border-white/10 p-8 flex flex-col">
      {icon && (
        <span className="absolute -top-4 left-1/2 -translate-x-1/2 w-9 h-9 rounded-full bg-[#A61C24] text-white flex items-center justify-center leading-none">
          {icon}
        </span>
      )}
      <p className="text-[10px] uppercase tracking-[0.3em] text-[#C64950] text-center mt-3 mb-6">
        {eyebrow}
      </p>
      <div className="flex-1 flex items-center justify-center min-h-[100px]">
        {children}
      </div>
      {priceLabel && (
        <div className="mt-6 pt-4 border-t border-white/10 text-center">
          <span className="price-tag text-xl text-[#C64950]">{priceLabel}</span>
          <span className="ml-2 text-xs uppercase tracking-[0.25em] text-white/40">en supplément</span>
        </div>
      )}
    </div>
  );
}

function SaisonColumn({ label, items }) {
  return (
    <div className="p-8">
      <p className="text-[10px] uppercase tracking-[0.3em] text-[#C64950] text-center mb-5">{label}</p>
      <ul className="space-y-3 text-center text-white/85">
        {items.map((it, i) => (
          <li key={i}>
            {i > 0 && (
              <div className="flex items-center justify-center gap-3 my-3" aria-hidden>
                <span className="w-8 h-px bg-white/15" />
                <span className="font-script text-base text-[#C64950]/80">ou</span>
                <span className="w-8 h-px bg-white/15" />
              </div>
            )}
            <span className="block font-serif text-lg leading-tight">{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MenuEnfantOverlay({ onClose }) {
  const [cfg, setCfg] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/menu-enfant"); setCfg(data); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="fixed inset-0 z-50 bg-[#0A0A0A] text-white overflow-y-auto tablet-mode" data-testid="menu-enfant-overlay">
      <SpecialHeader title="Menu Enfant" onClose={onClose} />
      <main className="px-10 md:px-16 py-14 max-w-2xl mx-auto">
        <div className="relative bg-[#141414] border border-white/10 px-10 md:px-14 py-16">
          {/* Price badge top right */}
          {cfg?.prix != null && (
            <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-[#A61C24] text-white px-8 py-2">
              <span className="price-tag text-2xl">{Number(cfg.prix).toFixed(2)} €</span>
            </div>
          )}

          <div className="text-center mb-10">
            <p className="font-script text-2xl text-[#C64950]">Pour les plus petits</p>
            <h3 className="font-serif text-5xl md:text-6xl mt-2">Menu Enfant</h3>
          </div>

          {/* PLATS */}
          <div className="mb-10">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#C64950] text-center mb-5">Plat au choix</p>
            <div className="space-y-4 text-center">
              {cfg?.plat_1 && <p className="font-serif text-2xl leading-tight">{cfg.plat_1}</p>}
              {cfg?.plat_1 && cfg?.plat_2 && (
                <div className="flex items-center justify-center gap-4">
                  <span className="w-12 h-px bg-white/20" />
                  <span className="font-script text-xl text-[#C64950]">ou</span>
                  <span className="w-12 h-px bg-white/20" />
                </div>
              )}
              {cfg?.plat_2 && <p className="font-serif text-2xl leading-tight">{cfg.plat_2}</p>}
            </div>
          </div>

          {/* DESSERTS */}
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#C64950] text-center mb-5">Dessert au choix</p>
            <div className="space-y-4 text-center">
              {cfg?.dessert_1 && <p className="font-serif text-xl leading-tight">{cfg.dessert_1}</p>}
              {cfg?.dessert_1 && cfg?.dessert_2 && (
                <div className="flex items-center justify-center gap-4">
                  <span className="w-12 h-px bg-white/20" />
                  <span className="font-script text-xl text-[#C64950]">ou</span>
                  <span className="w-12 h-px bg-white/20" />
                </div>
              )}
              {cfg?.dessert_2 && <p className="font-serif text-xl leading-tight">{cfg.dessert_2}</p>}
            </div>
          </div>

          <div className="mt-14 pt-6 border-t border-white/10 text-center">
            <p className="text-xs uppercase tracking-[0.35em] text-white/50">
              Enfant de moins de 12 ans
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}

// Notes affichées en bas de certains overlays (par nom de tuile)
const TILE_NOTES = {
  "Entrées":
    "Nos entrées sont servies uniquement accompagnées d'une assiette de frites maison (4 €) ou suivies d'un plat. — La direction",
};

function TileOverlay({ title, items, onClose }) {
  const note = TILE_NOTES[title];
  return (
    <div
      className="fixed inset-0 z-50 bg-[#0A0A0A] text-white overflow-y-auto tablet-mode"
      data-testid="tile-overlay"
    >
      <header className="sticky top-0 bg-[#0A0A0A] border-b border-white/10 px-10 md:px-16 py-6 grid grid-cols-[1fr_auto_1fr] items-center">
        <button
          onClick={onClose}
          data-testid="overlay-back"
          className="justify-self-start flex items-center gap-3 text-white/80 hover:text-white text-sm uppercase tracking-[0.25em]"
        >
          <ArrowLeft size={20} />
          Retour
        </button>
        <h2 className="font-serif text-3xl md:text-4xl text-[#C64950] text-center whitespace-nowrap">{title}</h2>
        <button
          onClick={onClose}
          aria-label="Fermer"
          className="justify-self-end text-white/70 hover:text-white p-2"
        >
          <X size={24} />
        </button>
      </header>

      <main className="px-10 md:px-16 py-12 max-w-4xl mx-auto">
        {items.length === 0 && (
          <p className="text-center text-white/60 text-xl italic">
            Rien de publié pour le moment dans cette catégorie.
          </p>
        )}
        <ItemList items={items} />
        {note && (
          <div
            data-testid={`overlay-note-${title}`}
            className="mt-12 pt-6 border-t border-white/10 text-center"
          >
            <p className="font-script text-lg text-[#C64950]/80 mb-1">Note du chef</p>
            <p className="text-white/70 text-base leading-relaxed max-w-2xl mx-auto italic">
              {note}
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

function ItemList({ items }) {
  // Group by sous_categorie if any item has one; else render as a flat list.
  const groups = items.reduce((acc, it) => {
    const key = (it.sous_categorie || "").trim();
    if (!acc[key]) acc[key] = [];
    acc[key].push(it);
    return acc;
  }, {});
  const keys = Object.keys(groups);
  const hasSubgroups = keys.some((k) => k !== "");

  if (!hasSubgroups) {
    return <FlatList items={items} />;
  }

  // Order: sub-groups first (in first appearance order), then ungrouped ("")
  const ordered = [
    ...keys.filter((k) => k !== ""),
    ...(groups[""] ? [""] : []),
  ];

  return (
    <div className="space-y-14">
      {ordered.map((key) => (
        <section key={key || "_none"}>
          {key && (
            <h3 className="text-base md:text-lg uppercase tracking-[0.35em] text-[#C64950] mb-6 flex items-center gap-4">
              <span>{key}</span>
              <span className="flex-1 h-px bg-white/15" />
            </h3>
          )}
          <FlatList items={groups[key]} />
        </section>
      ))}
    </div>
  );
}

function FlatList({ items }) {
  return (
    <ul className="space-y-6">
      {items.map((it) => (
        <li
          key={it.id}
          className="flex items-start gap-6 pb-5 border-b border-white/10"
          data-testid={`overlay-item-${it.id}`}
        >
          <div className="flex-1">
            <h3 className="font-serif text-3xl mb-1 leading-tight">{it.nom}</h3>
            {it.description && (
              <p className="text-white/65 text-lg italic">{it.description}</p>
            )}
          </div>
          {it.prix != null && (
            <span className="price-tag text-3xl text-[#C64950] whitespace-nowrap">
              {it.prix.toFixed(2)} €
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}
